import { InputMask } from "primereact/inputmask";
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useProfile } from "src/features/auth/hooks/useProfile";
import type { FocusRef } from "../types/FocusRef";

export interface PhoneFormData extends FieldValues {
  phone?: string;
}

type PhoneProps<T extends PhoneFormData> = {
  form: UseFormReturn<T, any, T>;
  required: boolean;
};

export interface PhoneRef extends FocusRef {}

export const PhoneField = forwardRef<PhoneRef, PhoneProps<any>>(
  function PhoneField<T extends PhoneFormData>(
    { form, required = true }: PhoneProps<T>,
    ref: React.ForwardedRef<PhoneRef>
  ) {
    const phoneRef = useRef<InputMask>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        phoneRef.current?.focus();
      },
    }));

    const { t } = useTranslation();
    const { phoneMask } = useProfile();

    const error = form.formState.errors.phone as
      | Record<string, any>
      | undefined;

    return (
      <>
        <InputMask
          id="phone"
          autoClear={false}
          mask={phoneMask}
          placeholder={phoneMask}
          invalid={!!error}
          {...form.register("phone" as Path<T>, {
            required: {
              value: required,
              message: t("Error.Required"),
            },
            validate: (value) => {
              if (required) {
                const unmasked = value?.replace(/[\s+()_\-]/g, "") ?? "";
                if (unmasked.length < 10) {
                  return t("Error.MinLength", { value: 10 });
                }
              }
              return true;
            },
          })}
          ref={(el) => {
            phoneRef.current = el;
            form.register("phone" as Path<T>).ref(el);
          }}
        />
        {error && <small className="p-error block">{error.message}</small>}
      </>
    );
  }
);
